"use client";

import { ChevronDown, Plus } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/utils";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Input } from "./input";
import { Label } from "./label";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

// SIM-1465 — shared multi-select primitive: a searchable popover that scales to 10+ options, with
// a trigger showing a compact summary so it works in tight layouts.
//
// The switch between "join with commas" and "{N} selected" is at 3, picked empirically: three
// short labels ("North, South, East") still fit a typical sidebar trigger, four always truncates.
const MAX_INLINE_SUMMARY = 3;

export interface MultiSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  options: string[];
  placeholder?: string;
  /**
   * Optional inline "+ Add option..." affordance. Mirrors the Select
   * primitive's sentinel pattern used in DashboardEditFields. Called with
   * the trimmed new value; the consumer owns the persistence + auto-select
   * flow. When omitted, the inline add row never renders.
   */
  onAddOption?: (newValue: string) => void;
  /** For testing / a11y wiring on the trigger button. */
  id?: string;
  triggerClassName?: string;
}

/**
 * Compose the trigger summary label for the current selection. Empty
 * selection falls back to the caller's placeholder; a short selection joins
 * with commas; a long selection shows the count.
 */
function summarizeSelection(value: string[], placeholder: string): string {
  if (value.length === 0) return placeholder;
  if (value.length <= MAX_INLINE_SUMMARY) return value.join(", ");
  return `${value.length} selected`;
}

function filterOptions(options: string[], query: string): string[] {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.length === 0) return options;
  return options.filter((opt) => opt.toLowerCase().includes(trimmed));
}

function shouldShowAddRow(
  options: string[],
  query: string,
  onAddOption: ((newValue: string) => void) | undefined,
): boolean {
  if (!onAddOption) return false;
  const trimmed = query.trim();
  if (trimmed.length === 0) return false;
  return !options.some((opt) => opt.toLowerCase() === trimmed.toLowerCase());
}

interface MultiSelectMenuProps {
  value: string[];
  options: string[];
  onChange: (next: string[]) => void;
  onAddOption?: (newValue: string) => void;
  id?: string;
}

/**
 * Popover body for {@link MultiSelect}: the search input, the filtered
 * checkbox list, and the optional inline "+ Add option" row. Extracted from
 * MultiSelect so the parent stays under the complexity-family caps (SIM-1894);
 * owns its own search query state since nothing outside the popover reads it.
 */
function MultiSelectMenu({ value, options, onChange, onAddOption, id }: MultiSelectMenuProps) {
  const [query, setQuery] = React.useState("");

  const filtered = filterOptions(options, query);
  const showAddRow = shouldShowAddRow(options, query, onAddOption);

  const toggle = (opt: string) => {
    const next = value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt];
    onChange(next);
  };

  const handleAdd = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    onAddOption?.(trimmed);
    setQuery("");
  };

  return (
    <PopoverContent
      align="start"
      data-slot="multi-select-popover"
      className="w-(--radix-popover-trigger-width) min-w-[12rem] p-2"
    >
      <Input
        size="sm"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search..."
        aria-label="Filter options"
        data-slot="multi-select-search"
        className="mb-2"
      />
      <div
        data-slot="multi-select-options"
        className="flex max-h-64 flex-col gap-1 overflow-y-auto"
      >
        {filtered.length === 0 && !showAddRow && (
          <p data-slot="multi-select-empty" className="px-1 py-1.5 text-xs text-muted-foreground">
            No options
          </p>
        )}
        {filtered.map((opt) => {
          const optionId = id ? `${id}-opt-${opt}` : `multi-select-opt-${opt}`;
          const checked = value.includes(opt);
          return (
            <Label
              key={opt}
              htmlFor={optionId}
              className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm font-normal hover:bg-accent"
            >
              <Checkbox id={optionId} checked={checked} onCheckedChange={() => toggle(opt)} />
              <span className="min-w-0 flex-1 truncate">{opt}</span>
            </Label>
          );
        })}
        {showAddRow && (
          <button
            type="button"
            data-slot="multi-select-add"
            onClick={handleAdd}
            className="flex items-center gap-2 rounded px-1 py-1 text-left text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <Plus className="h-3 w-3" aria-hidden="true" />
            <span className="truncate">Add &ldquo;{query.trim()}&rdquo;</span>
          </button>
        )}
      </div>
    </PopoverContent>
  );
}

function MultiSelect({
  value,
  onChange,
  options,
  placeholder = "Select options",
  onAddOption,
  id,
  triggerClassName,
}: MultiSelectProps) {
  const summary = summarizeSelection(value, placeholder);
  const isEmptySelection = value.length === 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          size="sm"
          data-slot="multi-select-trigger"
          data-empty={isEmptySelection ? "true" : "false"}
          className={cn(
            // SIM-1504: hold the trigger's background on hover — the outline Button's
            // `hover:bg-accent` reads right for an action but jarring for a form-value readout.
            // SIM-1512: no hover-state ring either; the focus ring is reserved for keyboard focus
            // (WCAG 2.4.7), and the chevron plus pointer cursor already carry clickability.
            // SIM-1510 added such a ring and was reverted.
            "w-full justify-between gap-1.5 px-3 text-sm font-normal hover:bg-background hover:text-foreground",
            isEmptySelection && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="min-w-0 flex-1 truncate text-left">{summary}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <MultiSelectMenu
        value={value}
        options={options}
        onChange={onChange}
        onAddOption={onAddOption}
        id={id}
      />
    </Popover>
  );
}

export { MultiSelect };
